import React from 'react';
import prolegionlogo1 from '../assets/prolegionlogo1.png';

const WATERMARK_LOGO_URL =
    'https://prolegion-assets.s3.ap-south-1.amazonaws.com/Assets/Prolegion_minimized_logo.png';

export const PAYMENT_BANK_DETAILS = {
    bankName: 'HDFC Bank Ltd.',
    accountNo: '50200092076798',
    ifsc: 'HDFC0000802',
    accountName: 'PROLEGION PRIVATE LIMITED',
};

const formatDateDMY = (iso) => {
    if (!iso) return '';
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, '0');
    const mon = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${mon}-${d.getFullYear()}`;
};

const getTaxRate = (currency) => (currency === 'USD' ? 5 : 18);

const getCurrencySymbol = (currency) => (currency === 'INR' ? '₹' : '$');

const formatMoneyPdf = (amount, currency) => {
    const n = parseFloat(amount) || 0;
    const sym = getCurrencySymbol(currency);
    const locale = currency === 'INR' ? 'en-IN' : 'en-US';
    const num = n.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${sym}${num}/-`;
};

const getIgstHeaderLabel = (currency) => (currency === 'USD' ? 'Tax@5%' : 'IGST@18%');

const CornerArtTL = () => (
    <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMinYMin meet"
        aria-hidden
    >
        <polygon fill="rgba(22, 65, 110, 0.92)" points="0,0 100,0 0,78" />
        <polygon fill="rgba(45, 120, 175, 0.7)" points="0,0 72,0 0,55" />
        <polygon fill="rgba(110, 185, 230, 0.5)" points="0,0 44,0 0,36" />
    </svg>
);

const CornerArtTR = () => (
    <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMaxYMin meet"
        aria-hidden
    >
        <polygon fill="rgba(18, 55, 95, 0.9)" points="100,0 100,72 28,0" />
        <polygon fill="rgba(50, 115, 170, 0.68)" points="100,0 100,52 48,0" />
        <polygon fill="rgba(95, 175, 220, 0.48)" points="100,0 100,34 68,0" />
        <polygon fill="rgba(140, 200, 235, 0.35)" points="100,0 100,18 86,0" />
    </svg>
);

const CornerArtBL = () => (
    <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMinYMax meet"
        aria-hidden
    >
        <polygon fill="rgba(20, 60, 105, 0.88)" points="0,100 0,22 92,100" />
        <polygon fill="rgba(48, 118, 172, 0.65)" points="0,100 0,42 68,100" />
        <polygon fill="rgba(90, 168, 218, 0.45)" points="0,100 0,62 44,100" />
        <polygon fill="rgba(130, 195, 235, 0.32)" points="0,100 0,82 22,100" />
    </svg>
);

const CornerArtBR = () => (
    <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMaxYMax meet"
        aria-hidden
    >
        <polygon fill="rgba(25, 70, 118, 0.9)" points="100,100 100,20 12,100" />
        <polygon fill="rgba(55, 125, 180, 0.68)" points="100,100 100,38 38,100" />
        <polygon fill="rgba(100, 180, 225, 0.46)" points="100,100 100,56 58,100" />
        <polygon fill="rgba(145, 205, 238, 0.3)" points="100,100 100,74 78,100" />
    </svg>
);

const CORNER_BOX = { width: '1.55in', height: '1.32in' };
const CORNER_INSET = '4px';
const CONTENT_PADDING_TOP = `calc(${CORNER_INSET} + ${CORNER_BOX.height} + 12px)`;

const InvoicePdfExport = ({ data, isPerformaInvoice }) => {
    if (!data) return null;

    const calculateSubTotal = () =>
        data.items.reduce((sum, item) => {
            const qty = parseFloat(item.qty) || 0;
            const rate = parseFloat(item.rate) || 0;
            return sum + qty * rate;
        }, 0);

    const calculateCGST = () =>
        data.items.reduce((sum, item) => {
            const qty = parseFloat(item.qty) || 0;
            const rate = parseFloat(item.rate) || 0;
            const tax = getTaxRate(item.currency);
            return sum + qty * rate * (tax / 100);
        }, 0);

    const calculateTotal = () => calculateSubTotal() + calculateCGST();

    const primaryCurrency = data.items[0]?.currency || 'INR';
    const taxSummaryLabel = primaryCurrency === 'USD' ? 'GST (5%)' : 'GST (18%)';

    const showInvoiceNumber = isPerformaInvoice || data.invoiceNumber;
    const invoiceNumberDisplay = isPerformaInvoice ? 'PROFORMA' : data.invoiceNumber;

    return (
        <div
            id="invoice-pdf-export"
            style={{
                position: 'relative',
                width: '8.5in',
                height: '11in',
                minHeight: '11in',
                maxHeight: '11in',
                boxSizing: 'border-box',
                margin: 0,
                padding: 0,
                overflow: 'hidden',
                backgroundColor: '#ffffff',
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                color: '#000',
            }}
        >
            {/* Corner art inset slightly from page edges */}
            <div
                style={{
                    ...CORNER_BOX,
                    position: 'absolute',
                    left: CORNER_INSET,
                    top: CORNER_INSET,
                    zIndex: 3,
                    lineHeight: 0,
                }}
            >
                <CornerArtTL />
            </div>
            <div
                style={{
                    ...CORNER_BOX,
                    position: 'absolute',
                    right: CORNER_INSET,
                    top: CORNER_INSET,
                    zIndex: 3,
                    lineHeight: 0,
                }}
            >
                <CornerArtTR />
            </div>
            <div
                style={{
                    ...CORNER_BOX,
                    position: 'absolute',
                    left: CORNER_INSET,
                    bottom: CORNER_INSET,
                    zIndex: 3,
                    lineHeight: 0,
                }}
            >
                <CornerArtBL />
            </div>
            <div
                style={{
                    ...CORNER_BOX,
                    position: 'absolute',
                    right: CORNER_INSET,
                    bottom: CORNER_INSET,
                    zIndex: 3,
                    lineHeight: 0,
                }}
            >
                <CornerArtBR />
            </div>

            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            >
                <img
                    src={WATERMARK_LOGO_URL}
                    alt=""
                    crossOrigin="anonymous"
                    style={{
                        width: 'min(88%, 780px)',
                        maxWidth: '780px',
                        height: 'auto',
                        opacity: 0.052,
                        filter: 'grayscale(1)',
                        objectFit: 'contain',
                    }}
                />
            </div>

            <div
                style={{
                    position: 'relative',
                    zIndex: 2,
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: `${CONTENT_PADDING_TOP} 0.52in 0.48in 0.52in`,
                }}
            >
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr minmax(200px, 32%)',
                        gridTemplateRows: 'auto auto',
                        columnGap: '16px',
                        rowGap: 0,
                        alignItems: 'start',
                    }}
                >
                    <div style={{ gridColumn: 1, gridRow: 1 }}>
                        <img
                            src={prolegionlogo1}
                            alt="ProLegion"
                            style={{ height: '56px', width: 'auto', display: 'block', marginBottom: '10px' }}
                        />
                    </div>
                    <div style={{ gridColumn: 1, gridRow: 2 }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
                            {data.fromCompany}
                        </div>
                        {[
                            ['Building No./Flat No.', data.fromFlat],
                            ['Road/street', data.fromStreet],
                            ['City/Town/village', data.fromCity],
                            ['District', data.fromDistrict],
                            ['State', data.fromState],
                            ['Pincode', data.fromPincode],
                            ['GST', data.fromGSTIN],
                        ].map(([label, val]) => (
                            <div key={label} style={{ fontSize: '13px', lineHeight: 1.35, marginTop: '2px' }}>
                                <span style={{ color: '#333' }}>{label}: </span>
                                <span>{val || ''}</span>
                            </div>
                        ))}
                    </div>
                    <div
                        style={{
                            gridColumn: 2,
                            gridRow: 2,
                            textAlign: 'right',
                            fontSize: '13px',
                            lineHeight: 1.55,
                            alignSelf: 'start',
                        }}
                    >
                        {isPerformaInvoice && (
                            <div style={{ fontWeight: 700, color: '#237ed9', marginBottom: '6px' }}>
                                Proforma Invoice
                            </div>
                        )}
                        <div>
                            <span style={{ color: '#555' }}>Invoice date: </span>
                            <span>{formatDateDMY(data.invoiceDate)}</span>
                        </div>
                        {(data.invoiceReference || '').trim() !== '' && (
                            <div>
                                <span style={{ color: '#555' }}>Reference: </span>
                                <span>{data.invoiceReference}</span>
                            </div>
                        )}
                        {showInvoiceNumber && (
                            <div>
                                <span style={{ color: '#555' }}>Invoice number: </span>
                                <span style={{ fontWeight: 600 }}>{invoiceNumberDisplay}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ marginTop: '22px', fontSize: '13px', lineHeight: 1.45 }}>
                    <div style={{ fontWeight: 700, marginBottom: '6px' }}>Bill to:</div>
                    {data.billToCompany ? (
                        <div style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                            {data.billToCompany}
                        </div>
                    ) : null}
                    {data.billToName ? (
                        <div style={{ fontWeight: 600, marginTop: '4px' }}>{data.billToName}</div>
                    ) : null}
                    {data.billToEmail ? (
                        <div>
                            <span style={{ color: '#333' }}>Email: </span>
                            {data.billToEmail}
                        </div>
                    ) : null}
                    {data.billToAddress ? (
                        <div style={{ marginTop: '4px', wordBreak: 'break-word' }}>
                            <span style={{ color: '#333' }}>Billing Address: </span>
                            {data.billToAddress}
                        </div>
                    ) : null}
                    {data.billToCity ? (
                        <div style={{ marginTop: '2px' }}>
                            <span style={{ color: '#333' }}>City/Town/village: </span>
                            {data.billToCity}
                        </div>
                    ) : null}
                    {data.billToState ? (
                        <div style={{ marginTop: '2px' }}>
                            <span style={{ color: '#333' }}>State: </span>
                            {data.billToState}
                        </div>
                    ) : null}
                    {(data.billToCountry || data.billToPincode) && (
                        <div style={{ marginTop: '2px' }}>
                            <span style={{ color: '#333' }}>Country: </span>
                            {data.billToCountry?.trim() || (data.billToPincode ? 'India' : '')}
                        </div>
                    )}
                    {data.billToPincode ? (
                        <div style={{ marginTop: '2px' }}>
                            <span style={{ color: '#333' }}>Pincode: </span>
                            {data.billToPincode}
                        </div>
                    ) : null}
                    {data.billToGSTIN ? (
                        <div style={{ marginTop: '4px' }}>
                            <span style={{ color: '#333' }}>GST: </span>
                            {data.billToGSTIN}
                        </div>
                    ) : null}
                </div>

                <div style={{ marginTop: '20px', pageBreakInside: 'avoid' }}>
                    <table
                        style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            border: '2px solid #000',
                            fontSize: '12px',
                            tableLayout: 'fixed',
                        }}
                    >
                        <colgroup>
                            <col style={{ width: '7%' }} />
                            <col style={{ width: '38%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '19%' }} />
                        </colgroup>
                        <thead>
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <th
                                    style={{
                                        border: '1px solid #000',
                                        padding: '10px 6px',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    S.No.
                                </th>
                                <th
                                    style={{
                                        border: '1px solid #000',
                                        padding: '10px 6px',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    Product/Service
                                </th>
                                <th
                                    style={{
                                        border: '1px solid #000',
                                        padding: '10px 6px',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    Service Cost
                                </th>
                                <th
                                    style={{
                                        border: '1px solid #000',
                                        padding: '10px 6px',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    {data.items.length > 0
                                        ? getIgstHeaderLabel(data.items[0].currency)
                                        : 'IGST@18%'}
                                </th>
                                <th
                                    style={{
                                        border: '1px solid #000',
                                        padding: '10px 6px',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    Amount
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((item, index) => {
                                const subtotal =
                                    (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
                                const taxRate = getTaxRate(item.currency);
                                const taxAmount = subtotal * (taxRate / 100);
                                const total = subtotal + taxAmount;
                                const cell = {
                                    border: '1px solid #000',
                                    padding: '10px 6px',
                                    textAlign: 'center',
                                    verticalAlign: 'middle',
                                };
                                return (
                                    <tr key={item.id}>
                                        <td style={{ ...cell, fontWeight: 700 }}>{index + 1}.</td>
                                        <td
                                            style={{
                                                ...cell,
                                                textAlign: 'center',
                                                fontWeight: 400,
                                            }}
                                        >
                                            {item.description ? (
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        textAlign: 'center',
                                                        whiteSpace: 'pre-line',
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {item.description}
                                                </div>
                                            ) : (
                                                <span
                                                    style={{
                                                        color: '#999',
                                                        fontStyle: 'italic',
                                                        display: 'block',
                                                        width: '100%',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Service description
                                                </span>
                                            )}
                                        </td>
                                        <td
                                            style={{
                                                ...cell,
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {formatMoneyPdf(subtotal, item.currency)}
                                        </td>
                                        <td
                                            style={{
                                                ...cell,
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            <span style={{ color: '#888' }}>{taxRate}%</span>{' '}
                                            <span>{formatMoneyPdf(taxAmount, item.currency)}</span>
                                        </td>
                                        <td style={{ ...cell, fontWeight: 700 }}>
                                            {formatMoneyPdf(total, item.currency)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div
                    style={{
                        marginTop: '14px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <div style={{ width: '19%', minWidth: '168px', fontSize: '13px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                            <span style={{ fontWeight: 600, color: '#222' }}>SUBTOTAL</span>
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {formatMoneyPdf(calculateSubTotal(), primaryCurrency)}
                            </span>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '8px',
                                marginTop: '6px',
                            }}
                        >
                            <span style={{ fontWeight: 600, color: '#222' }}>{taxSummaryLabel}</span>
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {formatMoneyPdf(calculateCGST(), primaryCurrency)}
                            </span>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '8px',
                                marginTop: '10px',
                                paddingTop: '8px',
                                borderTop: '1px solid #000',
                                fontWeight: 700,
                                fontSize: '15px',
                            }}
                        >
                            <span>TOTAL</span>
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {formatMoneyPdf(calculateTotal(), primaryCurrency)}
                            </span>
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: '18px', width: '100%', overflow: 'hidden' }}>
                    <div
                        style={{
                            float: 'left',
                            width: 'max-content',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            backgroundColor: 'rgba(230, 230, 230, 0.98)',
                            border: '1px solid #c8c8c8',
                            borderRadius: '10px',
                            padding: '12px 16px',
                            fontSize: '13px',
                            lineHeight: 1.5,
                        }}
                    >
                        <div style={{ fontWeight: 700, marginBottom: '8px' }}>Payment Details:</div>
                        <div>
                            <span style={{ color: '#333' }}>Bank Name: </span>
                            {PAYMENT_BANK_DETAILS.bankName}
                        </div>
                        <div>
                            <span style={{ color: '#333' }}>Bank Account No.: </span>
                            {PAYMENT_BANK_DETAILS.accountNo}
                        </div>
                        <div>
                            <span style={{ color: '#333' }}>IFSC Code: </span>
                            {PAYMENT_BANK_DETAILS.ifsc}
                        </div>
                        <div>
                            <span style={{ color: '#333' }}>Bank Account Name: </span>
                            {PAYMENT_BANK_DETAILS.accountName}
                        </div>
                    </div>
                    <div style={{ clear: 'both', height: 0, overflow: 'hidden' }} />
                </div>
            </div>
        </div>
    );
};

export default InvoicePdfExport;
